import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { observer } from 'mobx-react';
import ReactMarkdown from 'react-markdown';

import ErrorIcon from 'shared/components/ernie/error-icon';
import SuccessIcon from 'shared/components/ernie/success-icon';
import InfoIcon from 'shared/components/ernie/info-icon';
import {
  Container,
  DismissButton,
  DismissIcon,
  IconContainer,
  Message,
} from 'shared/components/ernie/styled-components';
import { ARIA_ROLES, ERNIE_TIMEOUTS, ERNIE_FADE_TIME } from 'shared/constants';
import useStores from 'shared/hooks/use-stores';

const iconMapping = {
  danger: ErrorIcon,
  error: ErrorIcon,
  info: InfoIcon,
  success: SuccessIcon,
};

const Ernie = observer(({ isIframe = false, iframeOffset = 0, parentOffset = 0 }) => {
  const { UI } = useStores();

  // "end time" for the current operation, tracks both hiding and showing
  const [endTime, setEndTime] = useState(0);
  // whether we're currently hidden or shown
  const [visible, setVisible] = useState(false);
  const [currentAlert, setCurrentAlert] = useState(UI.ernieQueue.length > 0 && UI.ernieQueue[0]);

  const { type, msg } = currentAlert ?? {};
  const IconComponent = _.get(iconMapping, type, iconMapping.danger);

  const handleDismiss = () => {
    if (visible) {
      UI.ernieQueue.shift();
      setVisible(false);
      setEndTime(Date.now() + ERNIE_FADE_TIME);
    }
  };

  // The container itself is also click-to-dismiss; stop propagation so one click on the button
  // consumes exactly one queued message instead of two.
  const handleDismissButtonClick = (event) => {
    event.stopPropagation();
    handleDismiss();
  };

  // handle the various timings...
  // we want to show the messages for their `timeout` duration in ms,
  // and then between messages we want to chill with visible=false for ERNIE_FADE_TIME ms
  // plus we need to handle getting re-rendered while maintaining correct timings
  useEffect(() => {
    const now = Date.now();
    // if endTime > 0 then we're either showing a message or in the fade time between
    if (endTime > 0) {
      if (!visible) {
        // in the delay in between messages so we wait, and then clear out state when we get there
        const timeoutId = setTimeout(() => {
          setEndTime(0);
        }, endTime - now);
        return () => clearTimeout(timeoutId);
      }
      // currently showing a message! (re-)schedule the timeout and keep on keeping on
      const timeoutId = setTimeout(() => {
        setCurrentAlert(UI.ernieQueue.shift());
        setVisible(false);
        setEndTime(Date.now() + ERNIE_FADE_TIME);
      }, endTime - now);
      return () => clearTimeout(timeoutId);
    }
    // end time is 0, so we're in an idle state here
    if (_.isEmpty(UI.ernieQueue)) {
      if (visible) {
        // this should never happen but might as well be safe :shrug:
        setVisible(false);
      }
      return undefined;
    }
    // idle and there's messages in the queue... showing time!
    // all we really need to do is setup the timeout and visible states because that will cause a re-render
    // and the above code will handle registering the timeout for when it's time to hide again
    //
    // Read the timeout off the item we're actually about to show (`nextAlert`), not off the
    // `currentAlert` state variable: `setCurrentAlert` below doesn't update `currentAlert` until
    // the next render, so reading `currentAlert?.timeout` in this same pass always saw the PREVIOUS
    // alert's timeout (or, for the very first alert of a session, the initial `false` state) and fell
    // through to ERNIE_TIMEOUTS.SHORT. In practice this meant the first ernie shown after any fresh
    // page load/navigation always displayed for 5s regardless of the timeout actually passed to
    // showErnie() (e.g. the 10s MED timeout used for checkout validation errors).
    const nextAlert = UI.ernieQueue[0];
    // Defensive skip: queue entries with empty/whitespace messages are shifted off here so the
    // component never renders (or schedules) a blank alert. Shifting mutates the observable queue,
    // so the effect re-runs on the queue change while we stay idle (endTime stays 0). This protects
    // against items already queued from a long-lived session or a caller bypassing showErnie.
    if (!nextAlert?.msg || typeof nextAlert.msg !== 'string' || !nextAlert.msg.trim()) {
      UI.ernieQueue.shift();
      return undefined;
    }
    setCurrentAlert(nextAlert);
    const messageTimeout = nextAlert?.timeout ?? ERNIE_TIMEOUTS.SHORT;
    setEndTime(now + messageTimeout);
    setVisible(true);
    return undefined;
  }, [UI.ernieQueue, UI.ernieQueue.length, currentAlert, endTime, visible]);

  return (
    <Container
      aria-live='assertive'
      aria-label={visible ? 'Alert' : ''}
      aria-labelledby={visible ? 'ernie-message' : ''}
      data-cy='ernie-container'
      data-test='ernie-container'
      data-testid='ernie-container'
      iframeOffset={iframeOffset}
      isIframe={isIframe}
      onClick={handleDismiss}
      parentOffset={parentOffset}
      role={visible ? ARIA_ROLES.ALERT : ARIA_ROLES.PRESENTATION}
      type={type}
      visible={visible}
    >
      <IconContainer>
        <IconComponent />
      </IconContainer>

      <Message id='ernie-message' data-cy='ernie-message' data-test='ernie-message' data-testid='ernie-message'>
        <ReactMarkdown>{msg}</ReactMarkdown>
      </Message>

      <DismissButton
        aria-hidden={!visible}
        aria-label='Dismiss notification'
        data-cy='ernie-dismiss'
        data-test='ernie-dismiss'
        data-testid='ernie-dismiss'
        onClick={handleDismissButtonClick}
        tabIndex={visible ? 0 : -1}
        type='button'
      >
        <DismissIcon />
      </DismissButton>
    </Container>
  );
});

export default Ernie;
